import { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectPermissions } from "../store/redux/slices/Permission-slice";
import { selectAuthData } from "../store/redux/slices/Auth-slice";
import { resolveFeatureFromPath, PUBLIC_ROUTE } from "./route-feature-map";
import { ACTIONS } from "./constants";
import { isAppAdmin } from "./app-admin";

const ALL_ACTIONS = Object.values(ACTIONS);

const PermissionContext = createContext({
    feature: null,
    actions: [],
    canView: false,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    canExport: false,
    hasPermission: () => false,
    isLoaded: false,
});

const fullAccess = (feature) => ({
    feature,
    actions: ALL_ACTIONS,
    canView: true,
    canCreate: true,
    canUpdate: true,
    canDelete: true,
    canExport: true,
    hasPermission: () => true,
    isLoaded: true,
});

export const PermissionProvider = ({ children, feature: featureOverride }) => {
    const location = useLocation();
    const permissions = useSelector(selectPermissions);
    const authData = useSelector(selectAuthData);
    const roleCode = authData?.user?.role_code;

    const value = useMemo(() => {
        const resolved = featureOverride || resolveFeatureFromPath(location.pathname);
        const feature = resolved === PUBLIC_ROUTE ? null : resolved;

        if (isAppAdmin(roleCode)) return fullAccess(feature);

        if (!permissions) {
            return {
                feature,
                actions: [],
                canView: false,
                canCreate: false,
                canUpdate: false,
                canDelete: false,
                canExport: false,
                hasPermission: () => false,
                isLoaded: false,
            };
        }

        if (!feature) return fullAccess(null);

        const actions = permissions[feature] || [];

        return {
            feature,
            actions,
            canView: actions.includes(ACTIONS.VIEW),
            canCreate: actions.includes(ACTIONS.CREATE),
            canUpdate: actions.includes(ACTIONS.UPDATE),
            canDelete: actions.includes(ACTIONS.DELETE),
            canExport: actions.includes(ACTIONS.EXPORT),
            hasPermission: (action) => actions.includes(action),
            isLoaded: true,
        };
    }, [location.pathname, permissions, roleCode, featureOverride]);

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePagePermissions = () => useContext(PermissionContext);

export default PermissionContext;
